const invitation = globalThis.__takeWisteariaReviewInvitation?.() ?? null;
const SECTION_SELECTOR = "[data-tea-reviews]";
const MORE_BUTTON_SELECTOR = "[data-review-more]";
const CARD_SELECTOR = ".tea-reviews__card";
const SCROLLER_SELECTOR = "[data-review-scroller]";
const RESULTS_SELECTOR = "[data-review-results]";
const ARIA_DISABLED = "aria-disabled";
const FOCUS_VISIBLE = ":focus-visible";
const SCROLL_LOCK_ATTRIBUTE = "scroll-lock";
/** The section's entrance: armed, then playing. */
const MOTION_CLASS = "tea-reviews--motion";
const IN_VIEW_CLASS = "is-in-view";
/** The reading sheet while it fades out. */
const CLOSING_CLASS = "is-closing";
/**
 * Shelves gliding after an arrow press or moving under a swipe, until their `scrollend`.
 * @type {WeakSet<Element>}
 */
const movingShelves = new WeakSet();

/**
 * Where `scrollend` is missing (Safari before 26.2), a shelf counts as settled once it has not scrolled for a moment.
 * @type {WeakMap<Element, ReturnType<typeof setTimeout>>}
 */
const settleTimers = new WeakMap();

/** @param {Element} scroller */
function markMoving(scroller) {
  movingShelves.add(scroller);
  if (!("onscrollend" in globalThis)) {
    clearTimeout(settleTimers.get(scroller));
    settleTimers.set(
      scroller,
      setTimeout(() => movingShelves.delete(scroller), 150),
    );
  }
}

/**
 * @typedef {import("@theme/tea-review-form").ReviewUI} ReviewUI
 * @typedef {import("@theme/tea-review-form").SubmissionState} SubmissionState
 * @typedef {(attempt: number) => Promise<Pick<typeof import("@theme/tea-review-form"), "openForm">>} FormLoader
 * @typedef {Object} OpenInvitation An invitation's form: its token and state, and how far its code has got.
 * @property {string} token
 * @property {SubmissionState} state
 * @property {FormLoader} load
 * @property {number} attempts Downloads of the form's code started so far.
 * @property {boolean} loading Whether one is under way.
 * @property {boolean} wired Whether the form's code is running.
 * @typedef {Object} MoreUI
 * @property {HTMLElement} root
 * @property {HTMLElement} results
 * @property {HTMLElement} list
 * @property {HTMLButtonElement} button
 * @property {HTMLElement} status
 * @property {string} sectionId
 * @property {string} productGid
 * @property {number} [pressedLeft] Where the shelf was scrolled when "Show more" was pressed.
 */

/**
 * In-flight "Show more" work per review section. Its presence marks the button busy and ignores clicks.
 * @type {WeakMap<HTMLElement, AbortController>}
 */
const moreRequests = new WeakMap();

/**
 * Listeners and observers of each initialized section, released when the editor unloads it.
 * @type {WeakMap<HTMLElement, AbortController>}
 */
const sectionLifetimes = new WeakMap();

/** Smooth scrolling, unless the visitor asked for reduced motion. @returns {ScrollBehavior} */
function scrollBehavior() {
  return globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "instant" : "smooth";
}

/** @param {string} token @returns {{product_gid?: string, exp?: number} | null} */
function claimsForFeedback(token) {
  try {
    if (token.length > 4096) {
      return null;
    }
    const encoded = token.split(".")[1] ?? "";
    const part = encoded.replaceAll("-", "+").replaceAll("_", "/");
    const padded = part.padEnd(Math.ceil(part.length / 4) * 4, "=");
    const bytes = Uint8Array.from(atob(padded), character => character.codePointAt(0) ?? 0);
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

/** @param {HTMLElement} messageElement @returns {Record<string, string>} */
function readMessages(messageElement) {
  // Attributes decode Shopify's HTML-escaped translations exactly once; JSON script text would not.
  const messages = { ...messageElement.dataset };
  delete messages.reviewMessages;
  return /** @type {Record<string, string>} */ (messages);
}

/** @param {HTMLElement} root @returns {ReviewUI | null} */
function readUI(root) {
  const form = root.querySelector("form");
  const status = root.querySelector("[data-review-status]");
  const button = form?.querySelector("button[type=submit]");
  const messageElement = root.querySelector("[data-review-messages]");
  if (
    !form ||
    !(status instanceof HTMLElement) ||
    !(button instanceof HTMLButtonElement) ||
    !(messageElement instanceof HTMLElement) ||
    !root.dataset.productGid
  ) {
    return null;
  }
  const heading = form.querySelector("[data-review-form-heading]");
  return {
    root,
    form,
    status,
    button,
    heading: heading instanceof HTMLElement ? heading : form,
    submitLabel: button.textContent ?? "",
    messages: readMessages(messageElement),
    productGid: root.dataset.productGid,
  };
}

/**
 * Lands an invitation reader on `target` below the sticky header, like native fragment entry
 * (`scroll-margin-block-start` supplies the offset), and focuses it once for assistive technology. The reader came
 * from a link, not the keyboard, so it draws no ring; Tab then reaches the form with one.
 * A page that arrived by an invitation link was held invisible by the bootstrap; it appears once this has scrolled.
 * @param {HTMLElement} target
 */
function land(target) {
  target.tabIndex = -1;
  target.addEventListener("blur", () => target.removeAttribute("tabindex"), { once: true });
  target.scrollIntoView({ behavior: "instant", block: "start" });
  target.focus({ preventScroll: true, focusVisible: false });
  globalThis.__revealWisteariaReviewInvitation?.();
}

/** @param {HTMLElement} root @returns {MoreUI | null} */
function readMoreUI(root) {
  const results = root.querySelector(RESULTS_SELECTOR);
  const list = results?.querySelector("[data-review-list]");
  const button = results?.querySelector(MORE_BUTTON_SELECTOR);
  // Outside the results, so it keeps announcing when a newer snapshot replaces them.
  const status = root.querySelector("[data-review-more-status]");
  const { sectionId, productGid } = root.dataset;
  if (
    !(results instanceof HTMLElement) ||
    !(list instanceof HTMLElement) ||
    !(button instanceof HTMLButtonElement) ||
    !(status instanceof HTMLElement) ||
    !sectionId ||
    !productGid
  ) {
    return null;
  }
  return { root, results, list, button, status, sectionId, productGid };
}

/**
 * Focuses `target` (a card gets a temporary `tabindex="-1"`). Focus moved after a click draws no ring; after a key
 * press it does.
 * @param {HTMLElement} target @param {boolean} viaKeyboard
 */
function focusQuietly(target, viaKeyboard) {
  if (!target.matches("button, [tabindex]")) {
    target.tabIndex = -1;
    target.addEventListener("blur", () => target.removeAttribute("tabindex"), { once: true });
  }
  target.focus({ preventScroll: true, focusVisible: viaKeyboard });
}

/**
 * Focuses `target` and keeps it in view with the smallest scroll, so readers stay where the list grew instead of
 * jumping to the top.
 * @param {Element | null | undefined} target @param {boolean} viaKeyboard
 */
function focusInView(target, viaKeyboard) {
  if (!(target instanceof HTMLElement) || !target.isConnected) {
    return;
  }
  focusQuietly(target, viaKeyboard);
  target.scrollIntoView({ behavior: "instant", block: "nearest", inline: "nearest" });
}

/**
 * Focuses the first new card and slides the shelf so it leads, the way the arrows move it. The page scrolls only as
 * far as it takes to show the whole shelf below the sticky header (the shelf's scroll margin).
 * @param {HTMLElement} item @param {boolean} viaKeyboard
 */
function revealItem(item, viaKeyboard) {
  const card = item.querySelector(CARD_SELECTOR);
  const scroller = item.closest(SCROLLER_SELECTOR);
  if (!(card instanceof HTMLElement) || !(scroller instanceof HTMLElement)) {
    focusInView(card ?? item, viaKeyboard);
    return;
  }
  focusQuietly(card, viaKeyboard);
  scroller.scrollIntoView({ behavior: "instant", block: "nearest", inline: "nearest" });
  const shelf = scroller.getBoundingClientRect();
  const padding = Number.parseFloat(getComputedStyle(scroller).scrollPaddingLeft) || 0;
  const left = scroller.scrollLeft + item.getBoundingClientRect().left - shelf.left - padding;
  scroller.scrollTo({ left, behavior: scrollBehavior() });
}

/**
 * Whether focus is still with the reviews: on "Show more", on a review, or fallen back to the page. Readers who moved
 * to another control while a page loaded, including the shelf's own arrows and "Read more", keep their place and
 * keystrokes; the status region still reports the result.
 * @param {MoreUI} ui
 */
function ownsFocus(ui) {
  const active = document.activeElement;
  if (!active || active === document.body || active === ui.button) {
    return true;
  }
  return ui.results.contains(active) && !active.matches("a[href], button, input, select, textarea");
}

/**
 * Whether the reader moved on while a page loaded though focus stayed with the reviews, as a click leaves it on
 * "Show more": they scrolled the shelf, or scrolled the page until the shelf left the window.
 * @param {MoreUI} ui
 */
function movedOn(ui) {
  const scroller = ui.list.closest(SCROLLER_SELECTOR);
  if (!(scroller instanceof HTMLElement)) {
    return false;
  }
  const shelf = scroller.getBoundingClientRect();
  return (
    Math.abs(scroller.scrollLeft - (ui.pressedLeft ?? scroller.scrollLeft)) > 4 ||
    shelf.bottom <= 0 ||
    shelf.top >= globalThis.innerHeight
  );
}

/** @param {HTMLButtonElement} button @param {boolean} busy */
function setBusy(button, busy) {
  // aria-disabled keeps the focused button in place; the disabled attribute would drop focus to the page.
  for (const name of [ARIA_DISABLED, "aria-busy"]) {
    if (busy) {
      button.setAttribute(name, "true");
    } else {
      button.removeAttribute(name);
    }
  }
}

/**
 * Renders this section for review page `page` of the same product URL through the Section Rendering API.
 * @param {MoreUI} ui @param {number} page @param {AbortSignal} signal @returns {Promise<HTMLElement | null>}
 */
async function fetchResults(ui, page, signal) {
  const url = new URL(globalThis.location.href);
  url.hash = "";
  url.searchParams.set("page", String(page));
  url.searchParams.set("section_id", ui.sectionId);
  // Revalidate: a cached fragment could hide a snapshot published since the page loaded.
  const response = await fetch(url, { cache: "no-cache", credentials: "same-origin", signal });
  if (!response.ok) {
    return null;
  }
  const html = await response.text();
  const section = new DOMParser().parseFromString(html, "text/html").querySelector(SECTION_SELECTOR);
  // A product URL that now redirects to another product must not put that product's reviews on this page.
  if (!(section instanceof HTMLElement) || section.dataset.productGid !== ui.productGid) {
    return null;
  }
  const results = section.querySelector(RESULTS_SELECTOR);
  return results instanceof HTMLElement ? results : null;
}

/** @param {MoreUI} ui @param {HTMLElement} fragment */
function copyProgress(ui, fragment) {
  const text = fragment.querySelector("[data-review-progress]")?.textContent?.trim() ?? "";
  const progress = ui.results.querySelector("[data-review-progress]");
  if (progress) {
    progress.textContent = text;
  }
  // The server counts every page up to this one, which is only what the reader sees when the list began at
  // page 1. A reader who arrived on ?page=N hears the new cards through focus instead of a wrong count.
  if (ui.results.dataset.startPage === "1") {
    setStatus(ui.status, text, false);
  }
}

/**
 * The count after "Show more" is only announced; a failure or a newer snapshot is also shown, because the reader
 * has to act on it.
 * @param {HTMLElement} status @param {string} text @param {boolean} notice
 */
function setStatus(status, text, notice) {
  status.textContent = text;
  status.toggleAttribute("data-notice", notice && text !== "");
}

/**
 * Appends the next page of the same snapshot after the current cards, landing each one in turn.
 * @param {MoreUI} ui @param {HTMLElement} fragment @param {boolean} viaKeyboard
 * @returns {boolean} false when the page is unusable
 */
function appendPage(ui, fragment, viaKeyboard) {
  const incoming = fragment.querySelector("[data-review-list]");
  if (!incoming) {
    return false;
  }
  const active = document.activeElement;
  const moveFocus = ownsFocus(ui) && !movedOn(ui);
  const known = new Set([...ui.list.querySelectorAll("li")].map(item => item.dataset.reviewId));
  const added = [...incoming.children].filter(item => {
    const id = item instanceof HTMLElement ? item.dataset.reviewId : undefined;
    if (id && known.has(id)) {
      return false;
    }
    known.add(id);
    return true;
  });
  added.forEach((item, index) => {
    if (!(item instanceof HTMLElement)) {
      return;
    }
    item.classList.add("is-new");
    item.style.animationDelay = `${index * 90}ms`;
    item.addEventListener("animationend", () => item.classList.remove("is-new"), { once: true });
  });
  const scroller = ui.list.closest(SCROLLER_SELECTOR);
  const scrolled = scroller?.scrollLeft ?? 0;
  ui.list.append(...added);
  ui.results.dataset.page = fragment.dataset.page ?? ui.results.dataset.page;
  copyProgress(ui, fragment);
  if (!fragment.querySelector(MORE_BUTTON_SELECTOR)) {
    // The last page is in: its slot at the end of the shelf goes too, so the shelf ends on a card.
    (ui.button.closest("[data-review-more-area]") ?? ui.button).remove();
  }
  if (!moveFocus && scroller && !movingShelves.has(scroller)) {
    // A reader who moved on keeps their place: snapping would otherwise follow a card it had only reached at the end.
    // A shelf still gliding or being swiped is left to finish.
    scroller.scrollTo({ left: scrolled, behavior: "instant" });
  }
  // New long reviews need their "Read more", and the shelf grew.
  measure(ui.root);
  if (moveFocus) {
    const first = added[0];
    if (first instanceof HTMLElement) {
      revealItem(first, viaKeyboard);
    } else {
      focusInView(ui.button.isConnected ? ui.button : [...ui.list.querySelectorAll(CARD_SELECTOR)].at(-1), viaKeyboard);
    }
  } else if (active === ui.button && !ui.button.isConnected) {
    // The last page took "Show more" away from a reader who moved on: focus goes to its first review, where it stands.
    const card = added[0] instanceof HTMLElement ? added[0].querySelector(CARD_SELECTOR) : null;
    if (card instanceof HTMLElement) {
      focusQuietly(card, viaKeyboard);
    }
  }
  return true;
}

/**
 * A newer snapshot replaced the loaded pages, so the count, summary, shelf and button start again from page 1.
 * The status region stays in place so assistive technology keeps announcing it.
 * @param {MoreUI} ui @param {HTMLElement} fresh @param {Record<string, string>} messages
 * @param {boolean} viaKeyboard
 */
function restartResults(ui, fresh, messages, viaKeyboard) {
  const active = document.activeElement;
  const ring = active instanceof HTMLElement && active !== ui.button ? active.matches(FOCUS_VISIBLE) : viaKeyboard;
  const moveFocus = ownsFocus(ui) && !movedOn(ui);
  // The new summary appears as it is, without an entrance that the old one may still have been waiting for.
  ui.root.classList.remove(MOTION_CLASS, IN_VIEW_CLASS);
  const freshHead = fresh.closest(SECTION_SELECTOR)?.querySelector("[data-review-head]");
  const head = ui.root.querySelector("[data-review-head]");
  const freshButton = fresh.querySelector(MORE_BUTTON_SELECTOR);
  ui.results.replaceChildren(...fresh.childNodes);
  // The live button keeps its listener and busy state; the rendered one is still hidden.
  if (freshButton) {
    ui.results.querySelector(MORE_BUTTON_SELECTOR)?.replaceWith(ui.button);
  } else {
    ui.button.remove();
  }
  if (head && freshHead) {
    head.replaceChildren(...freshHead.childNodes);
  }
  ui.results.dataset.publicationId = fresh.dataset.publicationId ?? "";
  ui.results.dataset.generation = fresh.dataset.generation ?? "";
  ui.results.dataset.page = fresh.dataset.page ?? "1";
  ui.results.dataset.startPage = ui.results.dataset.page;
  setStatus(ui.status, messages.updated ?? "", true);
  measure(ui.root);
  if (moveFocus) {
    focusInView(ui.results.querySelector(CARD_SELECTOR) ?? ui.results.firstElementChild, viaKeyboard);
  } else if (active instanceof HTMLElement && document.activeElement !== active) {
    // The control the reader moved to went with the old pages: "Show more" is back in its place, anything else is
    // gone, so focus goes to the first review, where it stands.
    const target = active.isConnected ? active : ui.results.querySelector(CARD_SELECTOR);
    if (target instanceof HTMLElement) {
      focusQuietly(target, ring);
    }
  }
}

/**
 * @param {MoreUI} ui @param {Record<string, string>} messages @param {AbortSignal} signal
 * @param {() => boolean} current Whether this request still owns the live section.
 * @param {boolean} viaKeyboard Whether "Show more" was pressed from the keyboard.
 * @returns {Promise<boolean>} false when the reviews could not be loaded or the request was superseded
 */
async function loadMore(ui, messages, signal, current, viaKeyboard) {
  const page = Number.parseInt(ui.results.dataset.page ?? "", 10) || 1;
  const next = await fetchResults(ui, page + 1, signal);
  if (!next || !current()) {
    return false;
  }
  if (
    next.dataset.publicationId === ui.results.dataset.publicationId &&
    next.dataset.generation === ui.results.dataset.generation
  ) {
    return appendPage(ui, next, viaKeyboard);
  }
  const first = await fetchResults(ui, 1, signal);
  if (!first || !current()) {
    return false;
  }
  restartResults(ui, first, messages, viaKeyboard);
  return true;
}

/**
 * Keeps the button where it was and returns focus to it, so the reader can simply try again.
 * @param {MoreUI} ui @param {Record<string, string>} messages @param {boolean} viaKeyboard
 */
function showMoreFailure(ui, messages, viaKeyboard) {
  setStatus(ui.status, messages.moreError ?? "", true);
  if (ownsFocus(ui)) {
    focusInView(ui.button, viaKeyboard);
  }
}

/** @param {HTMLElement} root @param {boolean} viaKeyboard */
async function showMore(root, viaKeyboard) {
  const ui = readMoreUI(root);
  const messageElement = root.querySelector("[data-review-messages]");
  if (!ui || moreRequests.has(root)) {
    return;
  }
  const messages = messageElement instanceof HTMLElement ? readMessages(messageElement) : {};
  ui.pressedLeft = ui.list.closest(SCROLLER_SELECTOR)?.scrollLeft;
  const controller = new AbortController();
  moreRequests.set(root, controller);
  setBusy(ui.button, true);
  if (ui.status.textContent === messages.moreError || ui.status.textContent === messages.updated) {
    // A one-off message that may repeat (another failure or snapshot) must change the live region to be announced.
    setStatus(ui.status, "", false);
  }
  const current = () => {
    if (!root.isConnected) {
      // A section removed without an unload event (for example by a morph) stops its work too.
      controller.abort();
    }
    return (
      moreRequests.get(root) === controller &&
      !controller.signal.aborted &&
      ui.results.isConnected &&
      ui.button.isConnected
    );
  };
  try {
    const updated = await loadMore(ui, messages, controller.signal, current, viaKeyboard).catch(() => false);
    if (!updated && current()) {
      showMoreFailure(ui, messages, viaKeyboard);
    }
  } finally {
    if (moreRequests.get(root) === controller) {
      moreRequests.delete(root);
    }
    setBusy(ui.button, false);
  }
}

/** @param {HTMLElement} root */
function initializeMore(root) {
  const ui = readMoreUI(root);
  if (!ui) {
    return;
  }
  // Progress counts are true only for a list that starts at page 1; a snapshot restart resets this.
  ui.results.dataset.startPage = ui.results.dataset.page ?? "1";
  // A button pressed from the keyboard reports no clicks (detail 0): only then does the moved focus show a ring.
  ui.button.addEventListener("click", event => {
    showMore(root, event.detail === 0);
  });
  // Progressive enhancement: without this module the <noscript> page links stay the way through.
  ui.button.hidden = false;
}

/**
 * Brings the shelf's controls in line with where it is scrolled: the arrows and progress line show only when the
 * cards overflow, and each arrow is disabled at its end.
 * @param {HTMLElement} root
 */
function measureShelf(root) {
  const scroller = root.querySelector(SCROLLER_SELECTOR);
  const tools = root.querySelector("[data-review-tools]");
  if (!(scroller instanceof HTMLElement) || !scroller.isConnected) {
    return;
  }
  const { scrollLeft, scrollWidth, clientWidth } = scroller;
  const overflow = scrollWidth - clientWidth;
  if (tools instanceof HTMLElement) {
    tools.hidden = overflow <= 4;
    for (const arrow of tools.querySelectorAll("[data-review-step]")) {
      const atEnd =
        arrow instanceof HTMLElement && arrow.dataset.reviewStep === "-1" ? scrollLeft < 4 : scrollLeft >= overflow - 4;
      if (atEnd) {
        arrow.setAttribute(ARIA_DISABLED, "true");
      } else {
        arrow.removeAttribute(ARIA_DISABLED);
      }
    }
    const bar = tools.querySelector("[data-review-progress-bar]");
    if (bar instanceof HTMLElement) {
      const seen = scrollWidth ? Math.min(1, (scrollLeft + clientWidth) / scrollWidth) : 1;
      bar.style.transform = `scaleX(${seen.toFixed(3)})`;
    }
  }
}

/**
 * Offers "Read more" only on the cards whose review is cut to four lines.
 * @param {HTMLElement} root
 */
function measureClamps(root) {
  // Every review is measured before any button changes, so the page is laid out once, not once per card.
  const cuts = [...root.querySelectorAll(CARD_SELECTOR)].map(card => {
    const body = card.querySelector(".tea-reviews__body");
    const button = card.querySelector("[data-review-read-more]");
    return body instanceof HTMLElement && button instanceof HTMLElement
      ? { button, cut: body.scrollHeight - body.clientHeight > 2 }
      : null;
  });
  for (const entry of cuts) {
    if (entry) {
      entry.button.hidden = !entry.cut;
    }
  }
}

/** @param {HTMLElement} root */
function measure(root) {
  measureClamps(root);
  measureShelf(root);
}

/**
 * The arrows move the shelf by the cards in view, so each press shows the next ones whole and none twice; on a phone,
 * where the next card peeks in, that is one card. Scroll snapping settles it on a card.
 * @param {HTMLElement} root @param {HTMLElement} arrow
 */
function stepShelf(root, arrow) {
  const scroller = root.querySelector(SCROLLER_SELECTOR);
  if (!(scroller instanceof HTMLElement) || arrow.getAttribute(ARIA_DISABLED) === "true") {
    return;
  }
  const direction = Number(arrow.dataset.reviewStep) || 0;
  const item = scroller.querySelector(".tea-reviews__item");
  const style = getComputedStyle(scroller);
  const view = scroller.clientWidth - Number.parseFloat(style.paddingLeft) - Number.parseFloat(style.paddingRight);
  let distance = view;
  if (item instanceof HTMLElement) {
    const step = item.getBoundingClientRect().width + (Number.parseFloat(style.columnGap) || 0);
    distance = Math.max(1, Math.floor((view + step - item.getBoundingClientRect().width) / step + 0.01)) * step;
  }
  markMoving(scroller);
  scroller.scrollBy({ left: direction * Math.round(distance), behavior: scrollBehavior() });
}

/**
 * Wires the shelf on the section itself, so the controls keep working after a newer snapshot replaces them.
 * @param {HTMLElement} root @param {AbortSignal} signal
 */
function initializeShelf(root, signal) {
  let frame = 0;
  root.addEventListener(
    "scroll",
    event => {
      if (!(event.target instanceof Element) || !event.target.matches(SCROLLER_SELECTOR)) {
        return;
      }
      markMoving(event.target);
      if (!frame) {
        frame = requestAnimationFrame(() => {
          frame = 0;
          measureShelf(root);
        });
      }
    },
    { capture: true, passive: true, signal },
  );
  root.addEventListener(
    "scrollend",
    event => {
      if (event.target instanceof Element) {
        movingShelves.delete(event.target);
      }
    },
    { capture: true, passive: true, signal },
  );
  root.addEventListener(
    "click",
    event => {
      const control =
        event.target instanceof Element ? event.target.closest("[data-review-step], [data-review-read-more]") : null;
      if (!(control instanceof HTMLElement) || !root.contains(control)) {
        return;
      }
      if (control.matches("[data-review-step]")) {
        stepShelf(root, control);
      } else {
        openSheet(root, control);
      }
    },
    { signal },
  );
  // Where reviews are cut changes with the section's size and with their words, which a browser's page translation
  // replaces after this has measured. Measuring in the next frame, outside the observers' own delivery, keeps the
  // height a new "Read more" adds from being reported as a resize loop.
  let pending = 0;
  const remeasure = () => {
    pending ||= requestAnimationFrame(() => {
      pending = 0;
      measure(root);
    });
  };
  if ("ResizeObserver" in globalThis) {
    let size = "";
    const observer = new ResizeObserver(entries => {
      const box = entries.at(-1)?.contentRect;
      const next = box ? `${box.width} ${box.height}` : size;
      if (next !== size) {
        size = next;
        remeasure();
      }
    });
    observer.observe(root);
    signal.addEventListener("abort", () => observer.disconnect(), { once: true });
  }
  const results = root.querySelector(RESULTS_SELECTOR);
  if (results && "MutationObserver" in globalThis) {
    const words = new MutationObserver(remeasure);
    words.observe(results, { childList: true, characterData: true, subtree: true });
    signal.addEventListener("abort", () => words.disconnect(), { once: true });
  }
  document.fonts?.ready.then(() => {
    if (!signal.aborted) {
      measure(root);
    }
  });
  signal.addEventListener(
    "abort",
    () => {
      cancelAnimationFrame(frame);
      cancelAnimationFrame(pending);
    },
    { once: true },
  );
  measure(root);
}

/** How much of the summary (or empty cup) is on screen before its entrance plays. */
const ENTRANCE_SHARE = 0.35;

/**
 * The summary's leaves fall and its bars pour, or the empty cup steeps, once, when a third of it is on screen.
 * Nothing is armed for visitors who ask for reduced motion, and the classes go once the motion is over, so later
 * content (a newer snapshot) simply appears.
 * @param {HTMLElement} root @param {AbortSignal} signal
 */
function initializeEntrance(root, signal) {
  const stage = root.querySelector(".tea-reviews__summary, .tea-reviews__empty");
  if (
    !stage ||
    !("IntersectionObserver" in globalThis) ||
    globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches
  ) {
    return;
  }
  root.classList.add(MOTION_CLASS);
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let done;
  const observer = new IntersectionObserver(
    entries => {
      // The first report comes as soon as it is observed, even for a sliver on screen, so the ratio is checked too.
      if (!entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= ENTRANCE_SHARE)) {
        return;
      }
      observer.disconnect();
      root.classList.add(IN_VIEW_CLASS);
      done = setTimeout(() => root.classList.remove(MOTION_CLASS, IN_VIEW_CLASS), 5000);
    },
    { threshold: ENTRANCE_SHARE },
  );
  observer.observe(stage);
  signal.addEventListener(
    "abort",
    () => {
      observer.disconnect();
      clearTimeout(done);
      root.classList.remove(MOTION_CLASS, IN_VIEW_CLASS);
    },
    { once: true },
  );
}

/**
 * @typedef {Object} SheetState
 * @property {HTMLElement} root The section the sheet belongs to.
 * @property {HTMLElement} trigger The "Read more" button that opened the sheet.
 * @property {string} reviewId The review it opened.
 * @property {boolean} ringOnEscape Whether focus returns with a ring after Escape: the trigger showed its ring when it
 * opened the sheet, or Tab was used in it.
 * @property {boolean} viaKeyboard Whether focus returns with a ring.
 * @property {boolean} ownsScrollLock Whether this sheet locked the page scroll.
 */

/** @type {WeakMap<HTMLDialogElement, SheetState>} Open reading sheets. */
const openSheets = new WeakMap();

/** @param {HTMLElement} root */
function readSheet(root) {
  const dialog = root.querySelector("[data-review-sheet]");
  const content = dialog?.querySelector("[data-review-sheet-content]");
  return dialog instanceof HTMLDialogElement && content instanceof HTMLElement ? { dialog, content } : null;
}

/**
 * Builds the reading sheet's copy of a card: its leaves and date, title, whole review and reviewer. The copy
 * carries no IDs from the card; the sheet is named by its own title, or by the reviewer when there is none.
 * @param {HTMLElement} root @param {Element} card @param {HTMLElement} content @returns {string} the naming ID
 */
function fillSheet(root, card, content) {
  const sectionId = root.dataset.sectionId ?? "";
  /** @type {Node[]} */
  const nodes = [];
  const top = card.querySelector(".tea-reviews__card-top");
  if (top) {
    nodes.push(top.cloneNode(true));
  }
  let labelId = "";
  const title = card.querySelector(".tea-reviews__card-title")?.textContent?.trim();
  if (title) {
    const heading = document.createElement("h3");
    heading.className = "tea-reviews__sheet-title";
    labelId = `ReviewSheetTitle-${sectionId}`;
    heading.id = labelId;
    heading.textContent = title;
    nodes.push(heading);
  }
  const body = document.createElement("p");
  body.className = "tea-reviews__sheet-body";
  body.textContent = card.querySelector(".tea-reviews__body")?.textContent?.trim() ?? "";
  nodes.push(body);
  const foot = card.querySelector(".tea-reviews__card-foot")?.cloneNode(true);
  if (foot instanceof HTMLElement) {
    foot.querySelectorAll("[id]").forEach(element => element.removeAttribute("id"));
    const name = foot.querySelector(".tea-reviews__name");
    if (name && !labelId) {
      labelId = `ReviewSheetName-${sectionId}`;
      name.id = labelId;
    }
    nodes.push(foot);
  }
  content.replaceChildren(...nodes);
  return labelId;
}

/**
 * Opens a long review whole in the reading sheet. The sheet itself takes focus, which draws no ring; Tab then
 * reaches its close button.
 * @param {HTMLElement} root @param {HTMLElement} trigger
 */
function openSheet(root, trigger) {
  const sheet = readSheet(root);
  const card = trigger.closest(CARD_SELECTOR);
  if (!sheet || !card || sheet.dialog.open) {
    return;
  }
  const labelId = fillSheet(root, card, sheet.content);
  if (labelId) {
    sheet.dialog.setAttribute("aria-labelledby", labelId);
  } else {
    sheet.dialog.removeAttribute("aria-labelledby");
  }
  const html = document.documentElement;
  const ownsScrollLock = !html.hasAttribute(SCROLL_LOCK_ATTRIBUTE);
  const item = trigger.closest("[data-review-id]");
  const reviewId = item instanceof HTMLElement ? (item.dataset.reviewId ?? "") : "";
  const ringOnEscape = trigger.matches(FOCUS_VISIBLE);
  openSheets.set(sheet.dialog, { root, trigger, reviewId, ringOnEscape, ownsScrollLock, viaKeyboard: true });
  sheet.dialog.showModal();
  if (ownsScrollLock) {
    html.setAttribute(SCROLL_LOCK_ATTRIBUTE, "");
  }
  sheet.content.scrollTop = 0;
  sheet.dialog.scrollTop = 0;
  sheet.dialog.focus({ preventScroll: true, focusVisible: false });
}

/**
 * Fades the sheet out like the newsletter pop-up, then closes it. Without its exit animation (reduced motion) it
 * closes at once; a second request while it fades changes nothing.
 * @param {HTMLDialogElement} dialog @param {boolean} viaKeyboard
 */
function closeSheet(dialog, viaKeyboard) {
  if (!dialog.open || dialog.classList.contains(CLOSING_CLASS)) {
    return;
  }
  const state = openSheets.get(dialog);
  if (state) {
    state.viaKeyboard = viaKeyboard;
  }
  dialog.classList.add(CLOSING_CLASS);
  const exit = dialog
    .getAnimations()
    .find(animation => animation instanceof CSSAnimation && animation.animationName === "tea-reviews-sheet-out");
  const close = () => {
    dialog.classList.remove(CLOSING_CLASS);
    if (dialog.open) {
      dialog.close();
    }
  };
  if (exit) {
    // Either way: when the fade ends, or when a native close or the Theme Editor cuts it short.
    exit.finished.then(close).catch(close);
  } else {
    close();
  }
}

/**
 * Where focus returns once the sheet closes: the "Read more" that opened it. A new width that no longer cuts the
 * review hides that button, and a newer snapshot replaces the cards; then it is the review's card, or the first one.
 * @param {SheetState} state @returns {HTMLElement | null}
 */
function returnTarget(state) {
  if (state.trigger.isConnected && !state.trigger.hidden) {
    return state.trigger;
  }
  const item = [...state.root.querySelectorAll("[data-review-id]")].find(
    element => element instanceof HTMLElement && element.dataset.reviewId === state.reviewId,
  );
  const readMore = item?.querySelector("[data-review-read-more]");
  if (readMore instanceof HTMLElement && !readMore.hidden) {
    return readMore;
  }
  const card = item?.querySelector(CARD_SELECTOR) ?? state.root.querySelector(CARD_SELECTOR);
  return card instanceof HTMLElement ? card : null;
}

/**
 * However the sheet closed, the page scrolls again and focus returns to the review it showed.
 * @param {HTMLDialogElement} dialog
 */
function finishSheet(dialog) {
  const state = openSheets.get(dialog);
  openSheets.delete(dialog);
  dialog.classList.remove(CLOSING_CLASS);
  dialog.querySelector("[data-review-sheet-content]")?.replaceChildren();
  if (!state) {
    return;
  }
  if (state.ownsScrollLock) {
    document.documentElement.removeAttribute(SCROLL_LOCK_ATTRIBUTE);
  }
  const target = returnTarget(state);
  if (target === state.trigger) {
    // The browser has already put focus back, with or without a ring by its own rules; it only redraws the ring
    // on a fresh focus.
    if (document.activeElement === target && target.matches(FOCUS_VISIBLE) !== state.viaKeyboard) {
      target.blur();
    }
    target.focus({ preventScroll: true, focusVisible: state.viaKeyboard });
  } else {
    focusInView(target, state.viaKeyboard);
  }
}

/** @param {HTMLElement} root @param {AbortSignal} signal */
function initializeSheet(root, signal) {
  const sheet = readSheet(root);
  if (!sheet) {
    return;
  }
  const { dialog } = sheet;
  /**
   * The dialog box fills its own padding, so only a pointer beyond its edge is on the backdrop.
   * @param {MouseEvent} event
   */
  const onBackdrop = event => {
    const box = dialog.getBoundingClientRect();
    return (
      event.target === dialog &&
      (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom)
    );
  };
  // Only a press that starts on the backdrop closes the sheet there, so selecting text and letting go outside does not.
  let pressedBackdrop = false;
  dialog.addEventListener(
    "pointerdown",
    event => {
      pressedBackdrop = onBackdrop(event);
    },
    { signal },
  );
  dialog.addEventListener(
    "click",
    event => {
      const target = event.target instanceof Element ? event.target : null;
      const fromBackdrop = pressedBackdrop;
      pressedBackdrop = false;
      if (target?.closest("[data-review-sheet-close]")) {
        closeSheet(dialog, event.detail === 0);
        return;
      }
      if (fromBackdrop && onBackdrop(event) && event.detail > 0) {
        closeSheet(dialog, false);
      }
    },
    { signal },
  );
  // Escape closes, as for any dialog, with the same fade. As in the site's other pop-ups, focus returns with a ring only
  // to a visitor moving around with the keyboard.
  dialog.addEventListener(
    "cancel",
    event => {
      event.preventDefault();
      closeSheet(dialog, openSheets.get(dialog)?.ringOnEscape ?? false);
    },
    { signal },
  );
  // The sheet has one control, so Tab stays on it rather than leaving for the browser's own controls.
  dialog.addEventListener(
    "keydown",
    event => {
      if (event.key !== "Tab") {
        return;
      }
      const state = openSheets.get(dialog);
      if (state) {
        state.ringOnEscape = true;
      }
      const close = dialog.querySelector("[data-review-sheet-close]");
      if (close instanceof HTMLElement) {
        event.preventDefault();
        close.focus({ preventScroll: true, focusVisible: true });
      }
    },
    { signal },
  );
  dialog.addEventListener("close", () => finishSheet(dialog), { signal });
  signal.addEventListener(
    "abort",
    () => {
      if (dialog.open) {
        dialog.close();
      }
      finishSheet(dialog);
    },
    { once: true },
  );
}

/** @param {Element} scope */
function abortWithin(scope) {
  for (const root of [scope, ...scope.querySelectorAll(SECTION_SELECTOR)]) {
    if (root instanceof HTMLElement) {
      moreRequests.get(root)?.abort();
      moreRequests.delete(root);
      sectionLifetimes.get(root)?.abort();
      sectionLifetimes.delete(root);
    }
  }
}

/** @type {WeakMap<HTMLElement, OpenInvitation>} Open invitation forms. */
const openInvitations = new WeakMap();

/**
 * Downloads the form's code. A browser remembers a failed import for good and never asks the server again, so each
 * retry asks for the same file under a new address.
 * @type {FormLoader}
 */
function importForm(attempt) {
  if (attempt === 0) {
    return import("@theme/tea-review-form");
  }
  // Inside a promise, so a browser that cannot resolve the address fails this try like any other download.
  return Promise.resolve().then(() => {
    const address = new URL(import.meta.resolve("@theme/tea-review-form"));
    address.searchParams.set("retry", String(attempt));
    return import(address.href);
  });
}

/**
 * Brings the form's code in and starts it. If the download fails the visitor is told, and pressing send (or opening
 * the invitation link again) tries once more; a try that the send button started goes on to send what was filled in.
 * @param {ReviewUI} ui @param {OpenInvitation} entry @param {boolean} pressed
 */
function wireForm(ui, entry, pressed) {
  if (entry.wired || entry.loading) {
    return;
  }
  entry.loading = true;
  const attempt = entry.attempts;
  entry.attempts += 1;
  entry
    .load(attempt)
    .then(({ openForm }) => {
      entry.loading = false;
      openForm(ui, entry.state, entry);
      entry.wired = true;
      if (ui.status.textContent === ui.messages.load) {
        ui.status.textContent = "";
      }
      if (pressed) {
        ui.form.requestSubmit();
      }
    })
    .catch(() => {
      entry.loading = false;
      ui.status.textContent = ui.messages.load ?? "";
    });
}

/** @param {HTMLElement} root @param {string | null} token @param {FormLoader} [load] */
export function initializeReviews(root, token = invitation, load = importForm) {
  if (root.dataset.initialized) {
    return;
  }
  root.dataset.initialized = "true";
  const lifetime = new AbortController();
  sectionLifetimes.set(root, lifetime);
  initializeMore(root);
  initializeShelf(root, lifetime.signal);
  initializeSheet(root, lifetime.signal);
  initializeEntrance(root, lifetime.signal);
  applyInvitation(root, token, load);
}

/**
 * Opens the review form for a usable invitation, or explains why an invitation link cannot be used.
 * A later invitation for a form already open replaces its token only while nothing has been sent.
 * @param {HTMLElement} root @param {string | null} token @param {FormLoader} [load]
 */
function applyInvitation(root, token, load = importForm) {
  const ui = readUI(root);
  // null means no invitation link; "" is a link the bootstrap could not use, which still needs feedback.
  if (!ui || token === null) {
    return;
  }
  const claims = claimsForFeedback(token);
  // Feedback only: intake and worker independently verify signed authorization and purchase.
  if (
    claims?.product_gid !== ui.productGid ||
    typeof claims.exp !== "number" ||
    !Number.isFinite(claims.exp) ||
    claims.exp * 1000 <= Date.now()
  ) {
    ui.status.textContent = ui.messages.invitation ?? "";
    land(ui.status);
    return;
  }
  const open = openInvitations.get(root);
  if (open) {
    if (!open.state.serialized && !open.state.sending) {
      open.token = token;
    }
    land(ui.heading);
    // A form whose code never arrived gets another try from the link opened again.
    wireForm(ui, open, false);
    return;
  }
  // Clear feedback about an earlier unusable link on this page.
  ui.status.textContent = "";
  ui.form.hidden = false;
  land(ui.heading);
  /** @type {SubmissionState} */
  const state = { serialized: null, submissionId: null, sending: false, accepted: false, rejected: false };
  /** @type {OpenInvitation} */
  const entry = { token, state, load, attempts: 0, loading: false, wired: false };
  openInvitations.set(root, entry);
  // The form's own code downloads now, alongside the landing, and wires it a moment later. Until then a send does
  // nothing, except that after a failed download it tries the download again.
  ui.form.addEventListener("submit", event => {
    event.preventDefault();
    wireForm(ui, entry, true);
  });
  wireForm(ui, entry, false);
}

/** @param {Document | Element} scope */
function initializeWithin(scope) {
  if (scope instanceof HTMLElement && scope.matches(SECTION_SELECTOR)) {
    initializeReviews(scope);
  }
  scope.querySelectorAll(SECTION_SELECTOR).forEach(root => {
    if (root instanceof HTMLElement) {
      initializeReviews(root);
    }
  });
}

initializeWithin(document);
document.addEventListener("shopify:section:load", event => {
  if (event.target instanceof Element) {
    initializeWithin(event.target);
  }
});
// The bootstrap captured an invitation from a same-page fragment change after this module loaded.
globalThis.addEventListener("wistearia:review-invitation", () => {
  const token = globalThis.__takeWisteariaReviewInvitation?.() ?? null;
  document.querySelectorAll(SECTION_SELECTOR).forEach(root => {
    if (root instanceof HTMLElement && root.dataset.initialized) {
      applyInvitation(root, token);
    }
  });
});
document.addEventListener("shopify:section:unload", event => {
  if (event.target instanceof Element) {
    abortWithin(event.target);
  }
});
